import { Component, Input, Output, EventEmitter, OnChanges, HostBinding } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment-timezone';
import {
  PublishingEvent,
  PublishingPlatform,
  PublishingStatus,
  isCancelEligible,
} from '../../../domain/publishing-status';
import { UserService } from '../../../domain/user-service';
import { ClippingStudioNavigationService } from '../../../infrastructure/clipping-studio/clipping-studio-navigation.service';
import { PublishingRepository } from '../../../infrastructure/repositories/publishing-status/publishing.repository';
import {
  PLATFORM_META,
  DEFAULT_PLATFORM_META,
  STATUS_META,
  GLOBE_ICON_PATH,
  COPY_LINK_ICON_PATH,
  MEDIA_ICON_PATH,
  DRAFT_DISPLAY_DEFAULTS,
  PlatformMeta,
  StatusMeta,
} from '../publishing-status.constants';

@Component({
  selector: '[evrideo-publishing-event-row]',
  templateUrl: './publishing-event-card.component.html',
  styleUrls: ['./publishing-event-card.component.scss'],
})
export class PublishingEventCardComponent implements OnChanges {
  @Input() event!: PublishingEvent;
  @Input() selected = false;
  @Input() selectionDisabled = false;
  @Output() selectionChange = new EventEmitter<boolean>();
  @Output() cancelled = new EventEmitter<void>();

  @HostBinding('class.publishing-table__row--selected')
  get isRowSelected(): boolean {
    return this.selected;
  }

  platformMeta: PlatformMeta = DEFAULT_PLATFORM_META;
  statusMeta: StatusMeta = { label: '', cssClass: '', icon: '' };
  displayTitle = '';
  displayPlatform = '';
  displayDestination = '';
  destinationIsPlaceholder = false;
  showPlatformIcon = true;
  /** Same format as /mam/uploadReport: MM-DD-YYYY HH:mm */
  formattedDateTime = '';
  /** Overlay icon: globe for website (France.tv), external-link for others. */
  overlayIconPath: string = '/assets/publishing-status/external-link.svg';
  readonly copyLinkIconPath = COPY_LINK_ICON_PATH;
  readonly mediaIconPath = MEDIA_ICON_PATH;
  isDraft = false;
  isReEditing = false;
  isCancelling = false;
  canCancel = false;
  showStatusMessage = false;

  constructor(
    private readonly toastr: ToastrService,
    private readonly clippingStudioNavigation: ClippingStudioNavigationService,
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly publishingRepository: PublishingRepository,
  ) {}

  ngOnChanges(): void {
    if (!this.event) return;

    this.isDraft = this.event.status === PublishingStatus.Draft;
    this.canCancel = isCancelEligible(this.event.status);

    const title = this.event.clipTitle?.trim();
    this.displayTitle =
      this.isDraft && !title ? DRAFT_DISPLAY_DEFAULTS.noTitle : (title ?? '');

    const platform = this.event.platform;
    if (this.isDraft && platform == null) {
      this.displayPlatform = DRAFT_DISPLAY_DEFAULTS.missingField;
      this.showPlatformIcon = false;
      this.platformMeta = DEFAULT_PLATFORM_META;
    } else {
      this.showPlatformIcon = true;
      this.platformMeta = PLATFORM_META[platform!] ?? DEFAULT_PLATFORM_META;
      if (platform != null && !PLATFORM_META[platform]) {
        console.warn('Unknown PublishingPlatform:', platform);
      }
      this.displayPlatform = this.platformMeta.label;
    }

    const account = this.event.account?.trim();
    this.destinationIsPlaceholder = this.isDraft && !account;
    this.displayDestination = this.destinationIsPlaceholder
      ? DRAFT_DISPLAY_DEFAULTS.missingField
      : (account ?? '');

    this.statusMeta = STATUS_META[this.event.status] ?? { label: this.event.status, cssClass: '', icon: '' };

    const message = this.event.errorMessage?.trim();
    this.showStatusMessage =
      !!message &&
      (this.event.status === PublishingStatus.SuccessWithWarnings ||
        this.event.status === PublishingStatus.Failed ||
        this.event.status === PublishingStatus.Cancelled);

    this.overlayIconPath =
      platform === PublishingPlatform.FranceTv
        ? GLOBE_ICON_PATH
        : '/assets/publishing-status/external-link.svg';

    this.formattedDateTime = this.formatCreatedAtDisplay();
  }

  onCheckboxChange(checked: boolean): void {
    if (this.selectionDisabled) {
      return;
    }
    this.selectionChange.emit(checked);
  }

  /**
   * Backend stores `created_at` as UTC; show in the user's display timezone (header clock / channel).
   */
  private formatCreatedAtDisplay(): string {
    const tz = this.userService.getDisplayTimezone();
    const raw = this.event.createdAt?.trim();
    if (!raw) {
      if (this.isDraft) {
        return moment.tz(tz).format('MM-DD-YYYY HH:mm');
      }
      return '';
    }

    const dt = moment.utc(raw);
    if (!dt.isValid()) {
      return '';
    }
    return dt.tz(tz).format('MM-DD-YYYY HH:mm');
  }

  onEditClick(): void {
    void this.clippingStudioNavigation.openPublicationEditor(this.event.id);
  }

  onEditInMamClick(): void {
    const mediaId = this.event.media_id;
    if (mediaId == null) {
      return;
    }
    void this.router.navigate(['/mam/editMedia', mediaId]);
  }

  async onReEditClick(): Promise<void> {
    if (this.isReEditing) return;
    this.isReEditing = true;
    try {
      await this.clippingStudioNavigation.reEditPublication(this.event.id);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Unable to re-edit publication';
      this.toastr.error(message);
    } finally {
      this.isReEditing = false;
    }
  }

  async onCancelClick(): Promise<void> {
    if (!this.canCancel || this.isCancelling) {
      return;
    }

    const confirmed = window.confirm(
      'Cancel this publication? In-progress work will stop. Content already on the platform is not removed.',
    );
    if (!confirmed) {
      return;
    }

    this.isCancelling = true;
    try {
      await this.publishingRepository.cancelPublication(this.event.id);
      this.toastr.success('Publication cancelled.');
      this.cancelled.emit();
    } catch (err: unknown) {
      const message = this.extractErrorMessage(
        err,
        'Unable to cancel publication',
      );
      this.toastr.error(message);
      this.cancelled.emit();
    } finally {
      this.isCancelling = false;
    }
  }

  onThumbnailError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = '/assets/icons/video-icon.svg';
  }

  onPublicationLinkCopied(): void {
    this.toastr.success('Publication link has been copied!');
  }

  private extractErrorMessage(err: unknown, fallback: string): string {
    if (err && typeof err === 'object') {
      const anyErr = err as {
        error?: { error?: string; message?: string | object };
        message?: string;
      };
      const bodyError = anyErr.error?.error;
      if (typeof bodyError === 'string' && bodyError.trim()) {
        return bodyError;
      }
      const bodyMessage = anyErr.error?.message;
      if (typeof bodyMessage === 'string' && bodyMessage.trim()) {
        return bodyMessage;
      }
      if (typeof anyErr.message === 'string' && anyErr.message.trim()) {
        return anyErr.message;
      }
    }
    if (err instanceof Error && err.message) {
      return err.message;
    }
    return fallback;
  }
}
